//
// Copyright ArangoDB GmbH, Cologne, Germany
// All rights reserved. See LICENSE.md in the project root for license information.
//

import { useFormikContext } from "formik";
import { useEffect, useMemo } from "react";
import { NewDeploymentFormValues } from "./newDeploymentForm.types";
import { useFetchArangoPlatformBundles } from "./useFetchArangoPlatformBundles";
import { sortArangoPlatformBundlesByPriorityDesc } from "./sortArangoPlatformBundlesByPriority";

export const useReinitializeArangoPlatformBundle = () => {
  const { values, setFieldValue } = useFormikContext<NewDeploymentFormValues>();
  const {
    data: bundles,
    isSuccess,
    isFetching,
  } = useFetchArangoPlatformBundles({
    regionId: values.regionId || "",
  });

  const sortedItems = useMemo(() => sortArangoPlatformBundlesByPriorityDesc(bundles?.items), [bundles?.items]);

  // Auto-select the highest-priority bundle when the available list changes (e.g. region switch).
  // In the edit flow the initial value comes from the deployment, so if it's still in the list
  // this effect is a no-op.
  useEffect(() => {
    if (!isSuccess || isFetching) return;

    if (!sortedItems.length) {
      if (values.arangodbPlatformBundle) {
        setFieldValue("arangodbPlatformBundle", "");
      }
      return;
    }

    const isCurrentInList = sortedItems.some((b) => b.id === values.arangodbPlatformBundle);
    if (!isCurrentInList) {
      setFieldValue("arangodbPlatformBundle", sortedItems[0]?.id || "");
    }
  }, [isSuccess, isFetching, sortedItems, values.arangodbPlatformBundle, setFieldValue]);
};
